import { Button, CustomPricingTableModal } from '@components'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'
import { MdOutlineRocketLaunch } from 'react-icons/md'
import styled from 'styled-components'
import { useBusinessContext } from '@root/context'
import { NocfoPlan, getSettingsPageUrl } from '@root/constants'

interface Props {
  header: string
  subHeader?: string
  features: string[]
  maxWidth?: string
}

export const TeaserView: React.FC<Props> = ({ header, subHeader, features, maxWidth = '100%' }) => {
  const [t] = useTranslation()
  const [showModal, setShowModal] = useState(false)

  const { slug, data: business } = useBusinessContext()

  const showPricingTable = business?.subscription_plan === NocfoPlan.FREE
  const planSettingsUrl = getSettingsPageUrl(slug, { page: 'plan' })

  return (
    <>
      <StyledTeaserView $maxWidth={maxWidth}>
        <StyledIcon>
          <MdOutlineRocketLaunch />
        </StyledIcon>
        <StyledContent>
          <StyledTrialBadge>{t('teaser.trialBadge')}</StyledTrialBadge>

          <h1>{header}</h1>
          {subHeader && <p>{subHeader}</p>}

          <FeatureWrapper>
            {features.map(feature => (
              <Feature key={feature}>
                <FaCheck /> <span>{feature}</span>
              </Feature>
            ))}
          </FeatureWrapper>
          <StyledFooter>
            <a href={t('link.pricing')} target="_blank" rel="noreferrer">
              <Button size="sm" type="button" isSecondary>
                {t('teaser.compare')}
              </Button>
            </a>
            {showPricingTable ? (
              <Button size="sm" intent="primary" onClick={() => setShowModal(true)}>
                {t('teaser.startTrial')}
              </Button>
            ) : (
              <a href={planSettingsUrl}>
                <Button size="sm" intent="primary" type="button">
                  {t('settings.sections.plan')}
                </Button>
              </a>
            )}
          </StyledFooter>
        </StyledContent>
      </StyledTeaserView>

      {showPricingTable && (
        <CustomPricingTableModal isVisible={showModal} handleOnClose={() => setShowModal(false)} />
      )}
    </>
  )
}

const StyledTrialBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: bold;
  padding: 0.15rem 0.3rem;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.neutralWhite};
  width: fit-content;
`

const StyledTeaserView = styled.div<{ $maxWidth: string }>`
  flex-wrap: wrap;
  border: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  display: flex;
  padding: ${({ theme }) => theme.spacing.md}rem;
  gap: ${({ theme }) => theme.spacing.md}rem;
  overflow: visible;
  background: white;
  max-width: ${({ $maxWidth }) => $maxWidth};
`

const StyledIcon = styled.div`
  width: 3rem;

  svg {
    color: ${({ theme }) => theme.colors.primary};
    width: 3rem;
    height: 3rem;
  }
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  color: ${({ theme }) => theme.colors.neutralBlack};
  gap: ${({ theme }) => theme.spacing.sm}rem;
  overflow: visible;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};

  h1 {
    font-size: ${({ theme }) => theme.fontSize.md}rem;
  }

  & > * {
    margin: 0;
  }
`

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  svg {
    fill: ${({ theme }) => theme.colors.primary};
  }
`

const FeatureWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.primary}22;
  padding-top: ${({ theme }) => theme.spacing.lg}rem;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md}rem;
`
